import React, { useState, useEffect, useRef } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { useSwipeable } from 'react-swipeable';
import { isGameFinished } from '../utils/constants';
import GameHeader from './GameHeader';
import H2hTable from './H2hTable';
import TotalsTable from './TotalsTable';
import SpreadsTable from './SpreadsTable';
import UpgradeButton from './UpgradeButton';

const NHLDataTable = ({
  data,
  gameTime,
  gameStatus,
  gameDate,
  lastUpdate,
  game,
  formatPlayerName,
  getTeamLogoUrl,
  getBookLogoUrl,
  isMobile,
  selectedMarket,
  selectedMarketName,
  bookmakers,
  isLoading,
  availableMarkets,
  onMarketSelection,
  showTiers,
  onTierToggle,
  masterTierFilter,
  onPlayerClick,
  onPlayerContextMenu,
  onOddsClick,
  clickedCellId,
  isCollapsed = false,
  onCollapseToggle,
  onSettingsClick
}) => {
  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', sport: 'nhl', ...game };

  const handleNameContextMenu = (player) => (e) => {
    if (!onPlayerContextMenu) return;
    const teamAbbr =
      player?.teamAbv ||
      (player?.team === 'away' ? safeGame.away : player?.team === 'home' ? safeGame.home : player?.team) ||
      player?.teamId ||
      null;
    onPlayerContextMenu(e, safeGame.gameIDmlb, player?.playerId, player?.name, teamAbbr);
  };


  // Helper function to create cell ID for animation
  const createCellId = (gameId, playerId, market, bookmaker, line, side) => {
    return `${gameId}_${playerId}_${market}_${bookmaker}_${line}_${side}`;
  };

  // Helper function to check if cell should be animated
  const getCellClassName = (gameId, playerId, market, bookmaker, line, side) => {
    const cellId = createCellId(gameId, playerId, market, bookmaker, line, side);
    return cellId === clickedCellId ? 'bet-add-animation' : '';
  };

  // Mobile carousel state
  const [mobileBookIndex, setMobileBookIndex] = useState(0);
  const booksPerPage = 5;

  // Use provided bookmakers or fall back to default list
  const allBookmakers = bookmakers && bookmakers.length > 0 ? bookmakers : ['fanduel', 'draftkings', 'betmgm', 'fanatics'];
  const totalPages = Math.ceil(allBookmakers.length / booksPerPage);

  // Determine if current market is mainline (h2h, totals, spreads)
  const isMainlineMarket = ['h2h', 'totals', 'spreads'].includes(selectedMarket);

  // Get visible books based on platform
  // For mobile player props: show best odds only (empty array)
  // For mobile mainline or desktop: show all/paginated books
  const bookLogos = isMobile && !isMainlineMarket
    ? [] // Empty for mobile player props - we'll show best odds instead
    : isMobile
      ? allBookmakers.slice(mobileBookIndex * booksPerPage, (mobileBookIndex + 1) * booksPerPage) // Mobile mainline - show paginated books
      : allBookmakers; // Desktop - show all books

  // Navigation functions for mobile
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToPrevBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === 0 ? totalPages - 1 : prev - 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const goToNextBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === totalPages - 1 ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Swipe handlers for mobile
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (isMobile && totalPages > 1) {
        goToNextBooks();
      }
    },
    onSwipedRight: () => {
      if (isMobile && totalPages > 1) {
        goToPrevBooks();
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  const oddsCellStyle = {
    width: '60px',
    minWidth: '60px',
    maxWidth: '60px',
    textAlign: 'center',
    fontSize: '0.95rem',
    letterSpacing: '-0.025em',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  // Tier color styling function (copied from NFLDataTable)
  const getCellStyle = (price, bestBookPrice, bestBookDiff, count, direction) => {
    const style = {
      fontWeight: 'bold',
      fontSize: isMobile ? '0.8rem' : '0.9rem'
    };
    const occurrences = [price].filter(p => p === bestBookPrice).length;

    // Always bold best odds when there are multiple books and it's the unique best price
    if (price === bestBookPrice && count > 1 && occurrences === 1) {

      // Apply edge tier colors if they meet the criteria
      if (bestBookDiff !== 1) {
        const diffValue = Math.abs(bestBookDiff);
        if (diffValue >= 0.25) {
          // Tier 1: Green with yellow text (original design)
          Object.assign(style, {
            backgroundColor: 'green',
            color: 'yellow',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        } else if (diffValue >= 0.15) {
          // Tier 2: Amber with dark text for better contrast
          Object.assign(style, {
            backgroundColor: '#F59E0B',
            color: '#1E40AF',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        } else if (diffValue >= 0.05) {
          // Tier 3: Blue - subtle, draws less attention than tier 1 & 2
          Object.assign(style, {
            backgroundColor: '#3B82F6',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        } else {
          // Best line but below tier thresholds - use amber text color
          Object.assign(style, { color: '#F59E0B' });
        }
      }
    }

    return style;
  };

  const getTooltipText = (price, bestBookPrice, bestBookDiff, count) => {
    const occurrences = [price].filter(p => p === bestBookPrice).length;
    if (price === bestBookPrice && bestBookDiff !== 1 && count > 1 && occurrences === 1) {
      const diffValue = Math.abs(bestBookDiff);
      if (diffValue > 0) {
        return `${(diffValue * 100).toFixed(1)}% edge`;
      }
    }
    return null;
  };

  // Helper function to normalize NHL positions to F or D
  const getNormalizedPosition = (player) => {
    const position = player.position || player.positionCode || player.position?.abbreviation;
    if (!position) return null;

    // Map NHL positions to F (Forward) or D (Defenseman)
    const positionStr = String(position).toUpperCase();

    // Forwards: forward, C (Center), LW (Left Wing), RW (Right Wing), W (Wing)
    if (['FORWARD', 'C', 'LW', 'RW', 'W', 'F', 'CENTER', 'LEFTWING', 'RIGHTWING', 'WING'].includes(positionStr)) {
      return 'F';
    }

    // Defensemen: defenseman, D (Defense), LD (Left Defense), RD (Right Defense)
    if (['DEFENSEMAN', 'D', 'LD', 'RD', 'DEFENSE'].includes(positionStr)) {
      return 'D';
    }

    // Goalie: goalie, G - don't show for player props typically
    if (['GOALIE', 'G'].includes(positionStr)) {
      return null;
    }

    return null;
  };

  // Helper function to get position sort priority
  const getPositionSortOrder = (player) => {
    const normalized = getNormalizedPosition(player);
    if (normalized === 'F') return 1; // Forwards first
    if (normalized === 'D') return 2; // Defensemen second
    return 3; // Unknown positions last
  };

  // Helper function to get best odds from API data for mobile display
  const findBestOdds = (player) => {
    // Use pre-calculated data from the API
    const bestBookPrice = player[`${selectedMarket}_bestBookPrice`];
    const bestBook = player[`${selectedMarket}_bestBook`];
    const bestBookDiff = player[`${selectedMarket}_bestBookDiff`];
    const bestBooks = player[`${selectedMarket}_bestBooks`] || [];

    // Count how many books have odds
    let bookCount = 0;
    allBookmakers.forEach(book => {
      const odds = player[`${selectedMarket}_${book}`];
      if (odds !== null && odds !== undefined) {
        bookCount++;
      }
    });

    return { bestOdds: bestBookPrice, bestBook, bestBookPrice, bestBookDiff, bookCount, bestBooks };
  };

  // Calculate mobile column width based on number of bookmakers
  const mobileBookWidth = Math.max(12, Math.floor(60 / bookLogos.length));
  const mobileNameWidth = 100 - (mobileBookWidth * bookLogos.length);

  const formatTime = (timeStr, gameStatus) => {
    if (!timeStr) return '';

    // If time is TBD, display TBD
    if (timeStr === 'TBD' || timeStr === 'TBA') return timeStr;

    // If the game is finished, don't show time
    if (gameStatus && isGameFinished(gameStatus)) {
      return '';
    }

    // For live games, show "LIVE" instead of time
    if (gameStatus === 'Live' || gameStatus === 'In Progress' || gameStatus === 'InProgress') {
      return 'LIVE';
    }

    // Handle PM/AM times - try to parse as Date first
    try {
      // If it looks like a time string (contains ':'), try to format it
      if (timeStr.includes(':')) {
        return timeStr; // Return as-is for now
      }
      return timeStr;
    } catch (e) {
      return timeStr;
    }
  };

  // NHL-specific live status formatter
  const formatNHLLiveStatus = (currentStatus, game, safeGame) => {
    const period = game.period || safeGame.period;
    const clock = game.clock || safeGame.clock;

    // Format period display for hockey
    let periodDisplay = '';
    if (!period) {
      // No period data, just show LIVE
      return (
        <span style={{
          backgroundColor: 'red',
          color: 'white',
          padding: '2px 6px',
          borderRadius: '4px',
          fontWeight: 'bold',
          fontSize: '0.75rem',
          display: 'inline-block'
        }}>
          LIVE
        </span>
      );
    } else if (period === 1) {
      periodDisplay = '1st';
    } else if (period === 2) {
      periodDisplay = '2nd';
    } else if (period === 3) {
      periodDisplay = '3rd';
    } else if (period === 4) {
      periodDisplay = 'OT';
    } else if (period > 4) {
      periodDisplay = `OT${period - 3}`;
    }

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        fontSize: '1.4rem',
        fontWeight: '600',
        color: 'yellow'
      }}>
        <div>{periodDisplay}</div>
        {clock && <div style={{ fontSize: '1.3rem', fontWeight: '500' }}>{clock}</div>}
      </div>
    );
  };

  // Check if data is empty or loading
  if (!data || data.length === 0) {
    if (isLoading) {
      return (
        <div className="table-group">
          <GameHeader
            gameTime={gameTime}
            gameStatus={gameStatus}
            game={game}
            getTeamLogoUrl={getTeamLogoUrl}
            isMobile={isMobile}
            isCollapsed={isCollapsed}
            onCollapseToggle={onCollapseToggle}
            formatLiveStatus={formatNHLLiveStatus}
            showScores={true}
            isLoading={isLoading}
          />
          <div className="text-center p-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="table-group">
        <GameHeader
          gameTime={gameTime}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={isMobile}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatNHLLiveStatus}
          showScores={true}
            isLoading={isLoading}
        />
        <div className="text-center p-3 text-muted">
          {(game?.gameIDmlb?.startsWith('locked_') || game?.gameId?.startsWith('locked_')) ? (
            <UpgradeButton onClick={onSettingsClick} />
          ) : (
            'No data available for this market'
          )}
        </div>
      </div>
    );
  }

  // Filter data based on showTiers setting and master tier filter
  let filteredData = data;

  // Binary markets (first goal, last goal, etc.) - don't filter by tier or isAlternate
  const isBinaryMarket = selectedMarket.includes('first') ||
                         selectedMarket.includes('last') ||
                         selectedMarket.includes('scorer');

  if (isBinaryMarket) {
    // Don't filter binary markets at all
    filteredData = filteredData;
  } else if (showTiers) {
    // When tiers toggle is ON, show all tiers regardless of dropdown selection
    filteredData = filteredData;
  } else if (masterTierFilter !== null) {
    // When tiers toggle is OFF, filter to the selected tier (defaults to 0.5)
    filteredData = filteredData.filter(player => {
      const playerTier = parseFloat(player.point);
      return playerTier === masterTierFilter;
    });
  } else {
    // Fallback: filter out alternates
    filteredData = filteredData.filter(player => !player.isAlternate);
  }

  // Sort the filtered data - away team players first, then home team players
  const sortedData = [...filteredData].sort((a, b) => {
    // First sort by team (away team first)
    const aTeam = a.team || '';
    const bTeam = b.team || '';
    if (aTeam === game.away_team && bTeam === game.home_team) return -1;
    if (aTeam === game.home_team && bTeam === game.away_team) return 1;

    // Then sort by name (with safety check for undefined names)
    const aName = a.name || '';
    const bName = b.name || '';
    const nameComparison = aName.localeCompare(bName);
    if (nameComparison !== 0) return nameComparison;

    // Then by point value (ascending)
    const aPoint = parseFloat(a.point) || 0;
    const bPoint = parseFloat(b.point) || 0;
    return aPoint - bPoint;
  });


  // Mainline markets (h2h, totals, spreads) - render similar to NFL
  if (['h2h', 'totals', 'spreads'].includes(selectedMarket)) {
    // Handle h2h data
    let h2hData = [];
    let totalsData = [];
    let spreadsData = [];

    if (selectedMarket === 'h2h') {
      // When h2h is selected, extract all three market types from the data
      filteredData.forEach(player => {
        if (player.playerId === 'team_away' || player.playerId === 'team_home') {
          h2hData.push(player);
        } else if (player.playerId?.startsWith('totals_')) {
          totalsData.push(player);
        } else if (player.playerId?.startsWith('spreads_')) {
          spreadsData.push(player);
        }
      });

      // Sort h2h data: away team first, then home team
      h2hData.sort((a, b) => {
        if (a.team === 'away' && b.team === 'home') return -1;
        if (a.team === 'home' && b.team === 'away') return 1;
        return 0;
      });
    } else if (selectedMarket === 'totals') {
      totalsData = filteredData.filter(p => p.playerId?.startsWith('totals_'));
    } else if (selectedMarket === 'spreads') {
      spreadsData = filteredData.filter(p => p.playerId?.startsWith('spreads_'));
    }


    return (
      <div className="table-group">
        <GameHeader
          gameTime={gameTime}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={isMobile}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatNHLLiveStatus}
          showScores={true}
            isLoading={isLoading}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Moneyline Table */}
        <H2hTable
          h2hData={h2hData}
          bookLogos={bookLogos}
          getBookLogoUrl={getBookLogoUrl}
          getCellStyle={getCellStyle}
          getTooltipText={getTooltipText}
          onOddsClick={onOddsClick}
          game={safeGame}
          oddsCellStyle={oddsCellStyle}
        />

        {/* Totals Table */}
        <TotalsTable
          totalsData={totalsData}
          bookLogos={bookLogos}
          getBookLogoUrl={getBookLogoUrl}
          getCellStyle={getCellStyle}
          getTooltipText={getTooltipText}
          onOddsClick={onOddsClick}
          game={safeGame}
          oddsCellStyle={oddsCellStyle}
        />

        {/* Spreads Table (Puck Line) */}
        <SpreadsTable
          spreadsData={spreadsData}
          bookLogos={bookLogos}
          getBookLogoUrl={getBookLogoUrl}
          getCellStyle={getCellStyle}
          getTooltipText={getTooltipText}
          onOddsClick={onOddsClick}
          game={safeGame}
          oddsCellStyle={oddsCellStyle}
        />
        </div>
      </div>
    );
  }

  // Check if this is an over/under market (non-binary markets)
  const isOverUnderMarket = [
    'player_goals',
    'player_assists',
    'player_points',
    'player_shots_on_goal',
    'player_total_saves',
    'player_blocked_shots',
    'player_power_play_points'
  ].includes(selectedMarket);

  // Debug logging for saves
  if (selectedMarket === 'player_total_saves') {
    console.log('NHL Saves Market - Raw data:', {
      dataLength: data?.length,
      firstPlayer: data?.[0],
      selectedMarket
    });
    console.log('NHL Saves Market - After filtering (before player props):', {
      filteredLength: filteredData.length,
      sortedLength: sortedData.length,
      masterTierFilter,
      showTiers
    });
  }

  // Player props - organize by team with separate tables
  const playersByTeam = {};

  // Organize all markets by team
  sortedData.forEach(player => {
    // Determine which team the player belongs to
    let teamKey = 'unknown';
    let teamName = 'Unknown Team';
    let teamLogo = null;

    // Try to determine team based on various fields
    if (player.team === safeGame.away || (player.teamId && player.teamId === safeGame.awayTeamId)) {
      teamKey = 'away';
      teamName = safeGame.awayTeamFull || safeGame.away;
      teamLogo = safeGame.awayLogo || getTeamLogoUrl(safeGame.away);
    } else if (player.team === safeGame.home || (player.teamId && player.teamId === safeGame.homeTeamId)) {
      teamKey = 'home';
      teamName = safeGame.homeTeamFull || safeGame.home;
      teamLogo = safeGame.homeLogo || getTeamLogoUrl(safeGame.home);
    } else {
      // Fallback - try to guess based on name or other indicators
      teamKey = player.team || 'unknown';
      teamName = player.team || 'Unknown Team';
      teamLogo = player.team ? getTeamLogoUrl(player.team) : null;
    }

    if (!playersByTeam[teamKey]) {
      playersByTeam[teamKey] = {
        name: teamName,
        logo: teamLogo,
        players: []
      };
    }

    playersByTeam[teamKey].players.push(player);
  });


  // Sort players within each team: Forwards first, then Defensemen, then by name
  Object.keys(playersByTeam).forEach(teamKey => {
    playersByTeam[teamKey].players.sort((a, b) => {
      // First sort by position (F before D)
      const posA = getPositionSortOrder(a);
      const posB = getPositionSortOrder(b);
      if (posA !== posB) {
        return posA - posB;
      }

      // Then sort by name
      const nameA = a.name || '';
      const nameB = b.name || '';
      const nameComparison = nameA.localeCompare(nameB);
      if (nameComparison !== 0) return nameComparison;

      // Then by point value (ascending)
      const aPoint = parseFloat(a.point) || 0;
      const bPoint = parseFloat(b.point) || 0;
      return aPoint - bPoint;
    });
  });

  // Debug logging after grouping
  if (selectedMarket === 'player_total_saves') {
    console.log('NHL Saves Market - After grouping:', {
      playersByTeam,
      teamKeys: Object.keys(playersByTeam),
      teamCounts: Object.entries(playersByTeam).map(([key, data]) => ({
        team: key,
        count: data.players.length
      }))
    });
  }

  return (
    <div className="table-group">
      <GameHeader
        gameTime={gameTime}
        gameStatus={gameStatus}
        game={game}
        getTeamLogoUrl={getTeamLogoUrl}
        isMobile={isMobile}
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
        formatLiveStatus={formatNHLLiveStatus}
        showScores={true}
            isLoading={isLoading}
      />

      {!(isMobile && isCollapsed) && (
      <div className="table-container">
        {Object.entries(playersByTeam).map(([teamKey, teamData], teamIndex) => (
        <div key={teamKey} style={{ marginBottom: teamIndex < Object.keys(playersByTeam).length - 1 ? '24px' : '0' }}>
          <Table bordered size="sm" className="text-center">
            <thead>
              <tr>
                <th style={{ width: isMobile ? '50%' : '200px' }}>NAME</th>
                {isMobile ? (
                  // Mobile: 3-column layout (Name | Book | Odds)
                  <>
                    <th style={{ width: '25%', textAlign: 'center' }}>BOOK</th>
                    <th style={{ width: '25%', textAlign: 'center' }}>ODDS</th>
                  </>
                ) : (
                  // Desktop: Show all books
                  bookLogos.map(book => (
                    <th key={`${selectedMarket}-${book}`} style={oddsCellStyle}>
                      <img
                        src={getBookLogoUrl(book)}
                        alt={`${book} logo`}
                        title={book}
                        style={{ height: '20px' }}
                      />
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {teamData.players.map((player, index) => {
                const isFirstRowForPlayer = index === 0 || teamData.players[index - 1].name !== player.name;

                // For mobile, get best odds
                const { bestOdds, bestBook, bestBookPrice, bestBookDiff, bookCount, bestBooks } = isMobile
                  ? findBestOdds(player)
                  : { bestOdds: null, bestBook: null, bestBookPrice: null, bestBookDiff: null, bookCount: 0, bestBooks: [] };

                // For over/under markets, render single row with stacked values in odds cells
                if (isOverUnderMarket) {
                  return (
                    <tr key={player.tierKey || player.id || `${player.name}-${index}`}>
                      <td style={{ textAlign: 'left', padding: isMobile ? '6px' : '8px', fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          {isFirstRowForPlayer ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px' }}>
                              <img
                                src={player.teamLogo?.startsWith('http') ? player.teamLogo : getTeamLogoUrl(player.teamLogo || player.team)}
                                alt={player.team}
                                style={{ width: '20px', height: '20px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                  e.target.style.margin = '0px';
                                }}
                              />
                              <span
                                onContextMenu={handleNameContextMenu(player)}
                                onClick={() => {
                                  if (onPlayerClick) {
                                    onPlayerClick(
                                      safeGame.gameIDmlb,
                                      player.playerId,
                                      player.name,
                                      selectedMarket,
                                      safeGame.away,
                                      safeGame.home,
                                      player.team || player.teamId,
                                      getNormalizedPosition(player),
                                      player.point
                                    );
                                  }
                                }}
                                style={{
                                  cursor: onPlayerClick ? 'pointer' : 'default'
                                }}
                              >
                                {formatPlayerName(player.name, player)}
                              </span>
                              {getNormalizedPosition(player) && (
                                <span style={{
                                  fontSize: isMobile ? '0.65rem' : '0.75em',
                                  color: '#9CA3AF',
                                  fontWeight: 'normal'
                                }}>
                                  ({getNormalizedPosition(player)})
                                </span>
                              )}
                            </div>
                          ) : (
                            <div style={{ marginLeft: '26px' }}></div>
                          )}
                          {/* Show point value on the right */}
                          {player.point !== null && player.point !== undefined && (
                            <small style={{
                              color: '#6B7280',
                              fontSize: isMobile ? '0.65rem' : '0.75rem',
                              flexShrink: 0,
                              minWidth: '60px',
                              textAlign: 'right'
                            }}>
                              {isMobile ? player.point : `Over ${player.point}`}
                            </small>
                          )}
                        </div>
                      </td>
                      {isMobile ? (
                        // Mobile: Show best book with only over odds
                        <>
                          <td style={{ padding: '8px 4px', textAlign: 'center', width: '25%' }}>
                            {(() => {
                              // Use API data for best book
                              const apiBestBook = player[`${selectedMarket}_bestBook`];
                              const apiBestBooks = player[`${selectedMarket}_bestBooks`] || [];

                              if (apiBestBook === 'multi') {
                                if (apiBestBooks.length === 2) {
                                  return (
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                                      {apiBestBooks.map(book => (
                                        <img
                                          key={book}
                                          src={getBookLogoUrl(book)}
                                          alt={book}
                                          style={{ height: '14px' }}
                                        />
                                      ))}
                                    </div>
                                  );
                                } else {
                                  return (
                                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                      {apiBestBooks.length} books
                                    </span>
                                  );
                                }
                              }
                              return apiBestBook && (
                                <img
                                  src={getBookLogoUrl(apiBestBook)}
                                  alt={apiBestBook}
                                  style={{ height: '16px' }}
                                />
                              );
                            })()}
                          </td>
                          {(() => {
                            // Use API data for best odds and edge styling
                            const bestBookPrice = player[`${selectedMarket}_bestBookPrice`];
                            const apiBestBook = player[`${selectedMarket}_bestBook`];
                            const bestBookDiff = apiBestBook === 'multi' ? 0 : player[`${selectedMarket}_bestBookDiff`];

                            // Count how many books have odds for this player
                            const booksWithOdds = allBookmakers.filter(b => {
                              const bookOdds = player[`${selectedMarket}_${b}_over`];
                              return bookOdds !== null && bookOdds !== undefined;
                            }).length;

                            const cellStyle = getCellStyle(
                              bestBookPrice,
                              bestBookPrice,
                              bestBookDiff,
                              booksWithOdds,
                              null
                            );

                            return (
                              <td style={{
                                padding: '8px 4px',
                                textAlign: 'center',
                                width: '25%',
                                fontSize: '0.9rem',
                                fontWeight: 'bold',
                                ...cellStyle
                              }}>
                                {bestBookPrice !== null && bestBookPrice !== undefined ? (bestBookPrice > 0 ? `+${bestBookPrice}` : bestBookPrice) : '-'}
                              </td>
                            );
                          })()}
                        </>
                      ) : (
                        // Desktop: Show all books with only over odds
                        bookLogos.map(book => {
                          const overOdds = player[`${selectedMarket}_${book}_over`];
                          const point = player.point;

                          const bestBookPrice = player[`${selectedMarket}_bestBookPrice`];
                          const bestBookDiff = player[`${selectedMarket}_bestBook`] === 'multi' ? 0 : player[`${selectedMarket}_bestBookDiff`];
                          const direction = player[`${selectedMarket}_${book}_direction`];

                          // Count how many books have odds for this player
                          const booksWithOdds = bookLogos.filter(b => {
                            const bookOdds = player[`${selectedMarket}_${b}_over`];
                            return bookOdds !== null && bookOdds !== undefined;
                          }).length;

                          return (
                            <td
                              key={`${selectedMarket}-${book}-${index}`}
                              onClick={() => {
                                if (overOdds && onOddsClick) {
                                  onOddsClick(
                                    safeGame.gameIDmlb,
                                    player.playerId,
                                    player.name,
                                    selectedMarket,
                                    book,
                                    overOdds,
                                    safeGame.away,
                                    safeGame.home,
                                    player.team,
                                    getNormalizedPosition(player),
                                    point,
                                    'over'
                                  );
                                }
                              }}
                              style={{
                                ...getCellStyle(
                                  overOdds,
                                  bestBookPrice,
                                  bestBookDiff,
                                  booksWithOdds,
                                  direction
                                ),
                                textAlign: 'center',
                                padding: '8px 4px',
                                cursor: overOdds ? 'pointer' : 'default',
                                opacity: player[`${selectedMarket}_${book}_over_isAvailable`] === false ? 0.25 : undefined
                              }}
                              title={getTooltipText(
                                overOdds,
                                bestBookPrice,
                                bestBookDiff,
                                booksWithOdds
                              )}
                            >
                              {overOdds !== null && overOdds !== undefined ? (
                                <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                                  {overOdds > 0 ? `+${overOdds}` : overOdds}
                                </div>
                              ) : null}
                            </td>
                          );
                        })
                      )}
                    </tr>
                  );
                }

                // Regular single-odds markets (goals, assists, points, first goal scorer)
                return (
                  <tr key={player.tierKey || player.id || `${player.name}-${index}`}>
                    <td style={{ textAlign: 'left', padding: isMobile ? '6px' : '8px', fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px' }}>
                          {isFirstRowForPlayer ? (
                            <>
                              <img
                                src={teamData.logo?.startsWith('http') ? teamData.logo : getTeamLogoUrl(teamData.logo)}
                                alt={teamData.logo}
                                style={{ width: '20px', height: '20px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                  e.target.style.margin = '0px';
                                }}
                              />
                              <span
                                onContextMenu={handleNameContextMenu(player)}
                                onClick={() => {
                                  if (onPlayerClick) {
                                    onPlayerClick(
                                      safeGame.gameIDmlb,
                                      player.playerId,
                                      player.name,
                                      selectedMarket,
                                      safeGame.away,
                                      safeGame.home,
                                      player.team || player.teamId,
                                      getNormalizedPosition(player)
                                    );
                                  }
                                }}
                                style={{
                                  cursor: onPlayerClick ? 'pointer' : 'default'
                                }}
                              >
                                {formatPlayerName(player.name, player)}
                              </span>
                              {getNormalizedPosition(player) && (
                                <span style={{
                                  fontSize: isMobile ? '0.65rem' : '0.75em',
                                  color: '#9CA3AF',
                                  fontWeight: 'normal'
                                }}>
                                  ({getNormalizedPosition(player)})
                                </span>
                              )}
                            </>
                          ) : (
                            <div style={{ marginLeft: '26px' }}></div>
                          )}
                        </div>
                        {player.point !== null && player.point !== undefined && (
                          <span style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', color: '#6b7280', marginLeft: '8px' }}>
                            {player.point === 'yes' ? 'Yes' :
                             player.point === 'no' ? 'No' :
                             player.point}
                          </span>
                        )}
                      </div>
                    </td>
                    {isMobile ? (
                      // Mobile: Show best book and best odds
                      <>
                        <td style={{ padding: '4px', textAlign: 'center', width: '25%' }}>
                          {bestBook === 'multi' ? (
                            bestBooks.length === 2 ? (
                              <div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                                {bestBooks.map(book => (
                                  <img
                                    key={book}
                                    src={getBookLogoUrl(book)}
                                    alt={book}
                                    style={{ height: '14px' }}
                                  />
                                ))}
                              </div>
                            ) : (
                              <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                {bestBooks.length} books
                              </span>
                            )
                          ) : bestBook && (
                            <img
                              src={getBookLogoUrl(bestBook)}
                              alt={bestBook}
                              style={{ height: '16px' }}
                            />
                          )}
                        </td>
                        <td
                          className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, bestBook, null, 'yes')}
                          style={{ padding: '6px', textAlign: 'center', cursor: 'pointer', width: '25%' }}
                          onClick={() => onOddsClick && onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, bestBook, bestOdds, safeGame.away, safeGame.home, player.team, player.positionCode)}
                          title={(() => {
                            // Use API's bestBookDiff directly
                            const bestBookDiff = player[`${selectedMarket}_bestBookDiff`] || 0;
                            return getTooltipText(bestOdds, bestBookPrice, bestBookDiff, bookCount);
                          })()}
                        >
                          {bestOdds !== null && (() => {
                            // Use API's bestBookDiff directly
                            const bestBookDiff = player[`${selectedMarket}_bestBookDiff`] || 0;
                            return (
                              <span style={getCellStyle(bestOdds, bestBookPrice, bestBookDiff, bookCount)}>
                                {bestOdds > 0 ? `+${bestOdds}` : bestOdds}
                              </span>
                            );
                          })()}
                        </td>
                      </>
                    ) : (
                      // Desktop: Show all books
                      bookLogos.map(book => {
                        const odds = player[`${selectedMarket}_${book}`];
                        const bestBookPrice = player[`${selectedMarket}_bestBookPrice`];
                        // Use API's bestBookDiff directly - already calculated correctly for NHL
                        const bestBookDiff = player[`${selectedMarket}_bestBookDiff`] || 0;
                        const direction = player[`${selectedMarket}_${book}_direction`];
                        const count = bookLogos.length;

                        const cellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, count, direction);
                        const tooltipText = getTooltipText(odds, bestBookPrice, bestBookDiff, count);

                        return (
                          <td
                            key={`${selectedMarket}-${book}-${index}`}
                            className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, player.point, 'yes')}
                            style={{...oddsCellStyle, ...cellStyle, cursor: odds ? 'pointer' : undefined, opacity: player[`${selectedMarket}_${book}_isAvailable`] === false ? 0.25 : undefined}}
                            onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.positionCode)}
                            title={tooltipText || undefined}
                          >
                            {odds ? (
                              <div style={{ fontSize: '0.9rem' }}>
                                {odds > 0 ? `+${odds}` : odds}
                              </div>
                            ) : null}
                          </td>
                        );
                      })
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        ))}
      </div>
      )}
    </div>
  );
};

export default NHLDataTable;